// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfoCard from "@plumberssupply/App/Widgets/InfoCardsWidget/InfoCard";
import { xScreen } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

interface InfoCard {
    fields: {
        cardTitle: string;
        image: string;
        mediumImage: string;
        smallImage: string;
        alt: string;
        title: string;
        description: string;
        openInNewTab: boolean;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
    };
}

const enum fields {
    cards = "cards",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.cards]: InfoCard[];
    };
}

type Props = OwnProps;

interface InfoCardsWidgetStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    content?: InjectableCss;
}

const infoCardsWidgetStyles: InfoCardsWidgetStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            ${xScreen("lte", 1200)} {
                flex-direction: column;
            }
            ${xScreen("lte", 768)} {
                justify-content: center;
                align-items: center;
            }
        `,
    },
    content: {
        css: css`
            width: 378px;
            ${xScreen(768, 1200)} {
                width: 100%;
            }
            ${xScreen("lt", 768)} {
                width: 352px;
                max-width: 100%;
            }

            &&&:last-of-type > div {
                margin-bottom: 0;
            }
        `,
    },
};

const styles = infoCardsWidgetStyles;

const InfoCardsWidget: React.FC<Props> = ({ fields: { cards } }) => {
    const links = useGetLinks(cards, o => o.fields.buttonLink);

    if (!cards || cards.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="Plumpers-InfoCardsWidget">
            <StyledWrapper {...styles.wrapper}>
                {cards.map((card, index) => (
                    <StyledWrapper {...styles.content} key={index.toString()}>
                        <InfoCard
                            openInNewTab={card.fields.openInNewTab}
                            imageSrc={card.fields.image}
                            mediumImageSrc={card.fields.mediumImage}
                            smallImageSrc={card.fields.smallImage}
                            altText={card.fields.alt}
                            title={card.fields.title}
                            description={card.fields.description}
                            buttonLabel={card.fields.buttonLabel}
                            link={links[index]?.url}
                        />
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCardsWidget,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.cards,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.cardTitle;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "cardTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Info Card",
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "mediumImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "smallImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "alt",
                        displayName: "Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Page" },
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
